import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Spin, message } from "antd";
import { loginSchema, LoginFormInputs } from "../utils/validation";
import Logo from "../asset/images/Area-56-logo.png";
import { useLogin } from "../hooks/useAuth"; // Import the useLogin hook that handles API calls

const LoginPage: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({ resolver: zodResolver(loginSchema) });

  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Use the login mutation hook
  const loginMutation = useLogin();
  const isLoading = loginMutation.isPending;

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      // User is already logged in, redirect to root
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Handle form submission using the hook
  const onSubmit = async (data: LoginFormInputs) => {
    setApiError(null);
    setLoginSuccess(false);

    try {
      // Use the login mutation
      await loginMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });

      // Show success message (we handle this in the component, not in the hook)
      message.success("Login successful!");
      setLoginSuccess(true);

      // Redirect after showing success message
      setTimeout(() => {
        // Using window.location.replace for most reliable redirection
        window.location.replace("/");
      }, 1000); // Allow 1 second to see the success message
    } catch (error: any) {
      // Set the error message from the hook
      setApiError(error.message);
    }
  };

  // Force redirect to root path
  const goToRoot = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.replace("/");
  };

  return (
    <div className="h-screen flex">
      {/* Side Image - Hidden on Small Screens */}
      <img
        src="https://res.cloudinary.com/duwdwr0r9/image/upload/v1742551546/9Z2A0951_1_odmltp.webp"
        alt="Side pics"
        className="w-1/2 bg-center hidden sm:block"
      />

      {/* Login Form */}
      <div className="w-full md:bg-white sm:w-1/2 flex justify-center items-center p-6 bg-gray-200">
        <div className="w-full max-w-md">
          {/* Logo */}
          <img
            src={Logo}
            alt="Logo"
            className="block sm:hidden mx-auto w-20 mb-4"
          />

          <h1 className="text-2xl font-bold text-center mb-6">Log In</h1>

          {/* Success Message */}
          {loginSuccess && (
            <div className="bg-green-100 p-3 rounded mb-4 border border-green-300 text-green-700">
              <p className="font-medium">Login Successful!</p>
              <p>Redirecting you now...</p>
            </div>
          )}

          {/* API Error Message */}
          {apiError && (
            <div className="bg-red-100 p-3 rounded mb-4 border border-red-300">
              <p className="font-medium text-red-700">Login Error</p>
              <p className="text-red-600">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Email Input */}
            <div className="mb-3">
              <input
                type="email"
                placeholder="Work Email"
                className={`w-full p-3 border rounded ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div className="mb-3">
              <input
                type="password"
                placeholder="Password"
                className={`w-full p-3 border rounded ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
                {...register("password")}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember Me + Forgot Password */}
            <div className="flex justify-between items-center mb-3">
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                Remember Me
              </label>
              <Link to="#" className="text-primary hover:underline">
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-secondary text-white py-2 rounded hover:bg-secondaryHover flex justify-center items-center"
              disabled={isLoading || loginSuccess}
            >
              {isLoading ? (
                <>
                  <Spin size="small" className="mr-2" /> Logging in...
                </>
              ) : loginSuccess ? (
                <>Redirecting...</>
              ) : (
                "Log In"
              )}
            </button>
          </form>

          {/* Optional manual redirect link */}
          {loginSuccess && (
            <div className="mt-4 text-center">
              <button
                onClick={goToRoot}
                className="text-primary hover:underline"
              >
                Click here if not redirected automatically
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
